export const en = {
  meta: { skip: "Skip to content", language: "Language", poweredBy: "Powered by" },
  login: {
    kicker: "Staff console",
    title: "Sign in",
    body: "For the registration desk, the live board and the organisers.",
    login: "Login",
    password: "Password",
    show: "Show password",
    hide: "Hide password",
    submit: "Sign in",
    invalid: "That login and password don't match.",
  },
  areas: {
    scan: "Scan QR",
    lookup: "Find by name",
    "walk-in": "Walk-in",
    board: "Live board",
    overview: "Overview",
    attendees: "Attendees",
    sessions: "Sessions",
    demographics: "Who is coming",
    users: "Users",
    event: "Event",
    activities: "Activities",
    content: "Content",
    email: "Email",
    survey: "Survey",
  },
  groups: { door: "Door", live: "Live", numbers: "Numbers", manage: "Manage" },
  shell: {
    menu: "Menu",
    close: "Close",
    signOut: "Sign out",
    collapse: "Collapse menu",
    expand: "Expand menu",
    signedInAs: "Signed in as {name}",
    lane: "Lane",
  },
  common: { save: "Save", cancel: "Cancel", optional: "(optional)", yes: "Yes", no: "No" },
  identity: {
    brand_owner: "Brand owner",
    creative_worker: "Creative worker",
    professional: "Professional",
  },
  errors: {
    generic: "Something went wrong. Please try again.",
    network: "We couldn't reach the server. Check the connection.",
    forbidden: "This page isn't for this login.",
    sessionExpired: "Your session has ended. Please sign in again.",
    rateLimited: "Too many tries. Please wait {s} seconds.",
    eventNotConfigured: "The event hasn't been set up on the server yet.",
    validation: "Some of what was sent isn't valid. Check the fields.",
    retry: "Try again",
  },
  fields: {
    required: "Required.",
    tooLong: "Too long.",
    tooShort: "Too short.",
    invalid: "This doesn't look right.",
    email: "Enter a valid email address.",
    phone: "Enter an Indonesian mobile number, such as 0812…",
    emailTaken: "Another account already uses this email.",
    phoneTaken: "Another account already uses this phone number.",
    notApplicable: "Doesn't apply to this choice.",
  },
  notFound: { title: "This page isn't here", home: "Go to my home page" },

  door: {
    walkInBadge: "Walk-in",
    verdict: {
      in: "Let them in",
      again: "Already inside: let them through",
      againSince: "Checked in at {time} at {lane} by {by}.",
      queued: "Saved on this device",
      queuedBody:
        "The server didn't answer. The scan is kept here and sent as soon as the connection is back. Let them in.",
      unknown: "QR not recognised",
      unknownBody: "This code isn't an attendee pass. Find the person by name instead:",
      error: "Couldn't check this one",
      merch: "Give merchandise",
      merchAgain: "Already collected: don't hand over again",
      merchAgainSince: "Collected at {time} at {lane} from {by}.",
    },
    log: {
      in: "Checked in",
      again: "Already inside",
      queued: "Saved offline",
      unknown: "Unknown QR",
      error: "Failed",
      merch: "Merchandise given",
      merchAgain: "Merchandise already collected",
    },
    merch: {
      offline:
        "No connection: a merchandise QR needs the server to check it. Wait for the connection, then scan again; don't hand anything over yet.",
      notFound: "Not a merchandise QR. Ask for the QR on the survey page (iforte.rekati.com/survey).",
    },
    queue: {
      waiting: "{n} waiting to send",
      sent: "{n} offline scans sent",
      partial: "{sent} sent, {kept} still waiting",
    },
    camera: {
      label: "Camera view",
      starting: "Starting the camera…",
      denied: "Camera access is blocked. Allow it in the browser's site settings, or use the field below.",
      none: "No camera found on this device. Use a handheld scanner or the field below.",
      insecure: "The camera needs a secure (https) address. Use the field below meanwhile.",
      failed: "The camera couldn't start. Use the field below.",
      switch: "Switch camera",
    },
    scan: {
      body: "Point the camera at the pass. A handheld scanner types into the field below.",
      manual: "Scanner or code",
      manualHint: "A handheld scanner types the code and presses Enter by itself.",
      send: "Check",
      waiting: "Waiting for a QR code…",
      recent: "On this device",
      noneYet: "Nothing scanned yet.",
    },
    lookup: {
      body: "For a lost or unreadable QR. Search by name or the last 4 digits of the phone number.",
      label: "Name or last 4 digits",
      placeholder: "Name, or the last 4 digits of the phone",
      hint: "Type at least two letters of the name, or exactly four digits.",
      tooBroad: "Too many people match. Type more of the name.",
      none: "Nobody matches. Check the spelling, or register them as a walk-in.",
      capped: "Showing the first 25. Type more to narrow it down.",
      in: "Already inside",
      inSince: "Inside since {time}",
      admit: "Let in",
    },
    walkIn: {
      body: "Register someone at the desk and let them in in one step. No email is sent.",
      who: "Who",
      name: "Full name",
      email: "Email",
      phone: "Mobile number",
      phoneHint: "Starts with 08, 62 or +62.",
      phoneAs: "Will be saved as {phone}",
      lang: "Language for later emails",
      langHint: "Used for anything we email them after the event.",
      profile: "Profile",
      profileBody: "Optional. Leave the type empty to skip the profile.",
      identity: "Type",
      identityNone: "Skip the profile",
      company: "Institution / company",
      brand: "Brand name",
      jobTitle: "Job title",
      industry: "Industry",
      industryOther: "Other…",
      industryOtherLabel: "Which industry",
      submit: "Register and let in",
      saving: "Saving…",
      note: "A phone number that's already registered just lets that person in.",
    },
  },

  board: {
    checkedIn: "Checked in",
    ofRegistered: "of {n} registered · {share}%",
    walkIns: "{n} walk-ins",
    perHour: "Arrivals per hour",
    recent: "Just arrived",
    none: "Arrivals will appear here.",
    fullscreen: "Full screen",
    toConsole: "Back to the console",
    exitFullscreen: "Exit full screen",
  },

  numbers: {
    overviewBody: "The headline figures, live from the door.",
    asOf: "Figures as of {time}, updating live.",
    checkedIn: "Checked in",
    ofRegistered: "{share} of registered",
    registered: "Registered",
    registeredNote: "Email confirmed",
    profiles: "Profiles complete",
    submitted: "Packs sent",
    noShow: "Not arrived",
    noShowNote: "Registered, not yet checked in",
    walkIns: "Walk-ins",
    walkInsNote: "Registered at the desk",
    arrivalsTitle: "Arrivals per hour",
    arrivalsBody: "Check-ins on the event day, Jakarta time.",
    noArrivals: "No check-ins yet.",
    registrationsTitle: "Registrations per day",
    registrationsBody: "Confirmed registrations; walk-ins are counted separately.",
    noRegistrations: "No confirmed registrations yet.",
  },

  export: {
    label: "Export",
    working: "Preparing…",
    xlsx: "Excel report",
    xlsxBody: "Summary, charts and the full attendee list.",
    csv: "CSV list",
    csvBody: "The attendee list, with survey answers once the survey runs.",
    surveyXlsx: "Survey Excel",
    surveyXlsxBody: "One row per response, with a filter on every column.",
    surveyCsv: "Survey CSV",
    surveyCsvBody: "The survey's responses only, one row each.",
    done: "Download ready",
  },

  sessions: {
    body: "How full each activity is, and how many of those holding a place came.",
    none: "No sessions. This event doesn't book sessions.",
    title: "Session",
    when: "When",
    capacity: "Capacity",
    confirmed: "Confirmed",
    waitlisted: "Waitlist",
    checkedIn: "Came",
    showUp: "Show-up",
    count: "Sessions",
    cameOf: "{n} of {of} came",
    allDay: "All day",
    tbd: "To be decided",
    full: "Full",
  },

  demographics: {
    body: "Who registered, from their profiles.",
    identity: "Type",
    registration: "Registered",
    industry: "Industry",
    howHeard: "How they heard",
    none: "Not answered",
    other: "other",
    online: "Online",
    onSite: "At the desk",
    empty: "No answers yet.",
  },

  attendees: {
    body: "Everyone registered, newest first. Contact details are for the organisers only.",
    search: "Search name, email or phone",
    count: "{n} people",
    none: "Nobody has registered yet.",
    noMatch: "Nobody matches that search or filter.",
    filterCheckIn: "Check-in",
    filterRegistration: "Registration",
    filterAll: "All",
    filterIn: "Checked in",
    filterNotIn: "Not checked in",
    filterOnline: "Online, confirmed",
    filterUnverified: "Online, not verified",
    filterWalkIn: "Walk-in",
    name: "Name",
    org: "Institution / brand",
    registered: "Registered",
    status: "Status",
    email: "Email",
    phone: "Phone",
    identity: "Type",
    jobTitle: "Job title",
    industry: "Industry",
    community: "Community member",
    verified: "Email confirmed",
    packSent: "Pack sent",
    checkedIn: "Checked in",
    unverified: "Not verified",
    walkIn: "Walk-in",
    inAt: "In {time}",
    pages: "Pages",
    page: "Page {page} of {pages}",
    prev: "Previous",
    next: "Next",
    adminNote: "Corrections to an attendee are made by a superuser.",
  },

  record: {
    gone: "This attendee no longer exists.",
    contact: "Contact",
    edit: "Correct",
    editBody:
      "Fix a mistyped name, email or phone. Fixing the email of someone still waiting for their code sends the code to the new address.",
    fields: { full_name: "Full name", email: "Email", phone: "Mobile number" },
    saved: "Saved",
    codeResent: "A new code went to {email}.",
    verification: {
      active: "Code sent, waiting",
      expired: "Code expired",
      locked: "Code locked after too many tries",
    },
    optOut: "Unsubscribed",
    profile: "Profile",
    noProfile: "No profile yet.",
    consent: "Consent",
    checkIn: "Check-in",
    checkedInLine: "In at {time}, {lane}, by {by}.",
    notIn: "Not checked in.",
    checkInNow: "Check in",
    checkedInNow: "{name} is checked in",
    alreadyIn: "Already checked in at {time}",
    inAtShort: "Checked in {time}",
    undo: "Undo check-in",
    undoTitle: "Undo this check-in?",
    undoBody:
      "For a mis-scan or a walk-in let in by mistake. The board's list isn't changed; its total corrects itself.",
    undoConfirm: "Undo",
    undone: "Check-in removed",
    notCheckedIn: "This person isn't checked in.",
    confirmed: "Confirmed",
    waitlist: "Waitlist #{n}",
    release: "Release",
    released: "Place released",
    promoted: "The next person on the waitlist got the place.",
    bookLabel: "Book a session…",
    book: "Book",
    join: {
      confirmed: "Booked",
      waitlisted: "Session full: put on the waitlist",
      already: "Already holding this session",
    },
    rsvpDisabled: "Session booking is switched off for this event.",
    sessionCancelled: "That session is cancelled.",
    capacityNotSet: "That session has no capacity set yet.",
    email: "Email",
    resendCode: "Resend the code",
    codeSent: "Code sent",
    codeUntil: "Valid until {time}.",
    alreadyVerified: "Already verified.",
    noCodeInFlight: "No code is waiting. They can register again with the same phone.",
    resendPack: "Resend the pack",
    packQueued: "Pack queued",
    packAlready: "A pack is already on its way",
    notVerified: "Their email isn't confirmed yet.",
    profileIncomplete: "Their profile isn't complete, so there's no pack to send.",
    delete: "Delete attendee",
    deleteTitle: "Delete this attendee?",
    deleteBody:
      "Releases their places, removes their check-in and cancels queued email. Their phone and email stay taken. Only an operator at the database can undo it.",
    deleteConfirm: "Delete",
    deleted: "Attendee deleted",
  },

  profileEdit: {
    open: "Edit profile",
    body: "The same questions and rules as the registration form. Required ones are marked *.",
    save: "Save profile",
    saved: "Profile saved",
    savedBody: "No email goes out for this; use Resend the pack if they still need one.",
    required: "required",
    noConsentTitle: "Only the attendee can give consent",
    noConsentBody:
      "This profile was saved without the attendee's consent (at the desk). It can be edited here once they give it themselves on the /rsvp page.",
    noProfileBody:
      "There is no profile yet. The attendee fills it in themselves, with their consent, on the /rsvp page.",
    identityChange: "Changing the type clears the answers that belong only to the old one.",
    interestOther: "Other interests",
    blankKeeps: "An emptied field keeps the answer already stored; it is not deleted.",
    missing: "Required questions left unanswered: {n}.",
    q: {
      identity_type: "Type",
      personal_instagram: "Personal Instagram",
      how_heard: "How they heard about the event",
      what_excites_you: "What excites them",
      industry: "Industry",
      interests: "Interests",
      brand_name: "Brand name",
      about_brand: "About the brand",
      brand_instagram: "Brand Instagram",
      company_name: "Institution / company",
      job_title: "Job title",
      community_member: "Member of {community}",
      community_member_plain: "Community member",
    },
  },
  kara: {
    launch: "Ask Kara",
    online: "Online",
    offline: "Offline",
    unset: "Not set up",
    unsetHint: "Kara isn't set up on this server yet.",
    offlineHint: "Kara is offline right now",
    intro:
      "Ask about registrations, attendance and the lineup. Kara answers with figures, not individual attendees.",
    try1: "How many people have checked in?",
    try2: "Registrations per day this week?",
    try3: "What share of registrants are professionals?",
    ask: "Ask Kara…",
    send: "Send",
    thinking: "Kara is thinking…",
    consulted: "consulted: {tools}",
    restart: "New conversation",
    unavailable: "Kara is offline. Try again in a moment.",
  },

  users: {
    body: "The logins for the desk devices, the board and the organisers.",
    add: "Add login",
    addBody: "Desk devices use a plain login such as frontdesk_7; people use their email.",
    none: "No logins yet.",
    you: "You",
    active: "Active",
    inactive: "Off",
    activeFor: "{name} can sign in",
    role: "Role for {name}",
    roleLabel: "Role",
    roleHint: {
      frontdesk: "The desk: scan, find by name, walk-in.",
      monitor: "The live board and the headline figures.",
      admin: "Every figure, the attendee list, the export and Kara.",
      superuser: "Everything, plus these management pages.",
    },
    more: "More for {name}",
    lastLogin: "Last sign-in {time}",
    neverLoggedIn: "Never signed in",
    reset: "Reset password",
    revoke: "Sign out everywhere",
    revoked: "{name} is signed out everywhere",
    updated: "{name} updated",
    selfChange: "You can't change your own role or switch yourself off. Ask another superuser.",
    notFound: "That login no longer exists.",
    login: "Login",
    loginHint: "Lower case; frontdesk_7 or an email.",
    loginTaken: "That login already exists.",
    name: "Name",
    password: "Password",
    passwordHint: "At least 12 characters. Leave empty to generate one.",
    passwordShort: "At least 12 characters.",
    create: "Create login",
    created: "{name} created",
    secretTitle: "Password for {name}",
    secretBody: "Shown this once. Copy it now: it can't be shown again, only reset.",
    copy: "Copy",
    copied: "Copied",
    secretDone: "I've saved it",
  },

  event: {
    body: "The event's name, schedule and the details attendees receive.",
    basics: "Basics",
    name: "Event name",
    date: "Event date",
    deadline: "Session booking closes",
    deadlineHint: "Empty means it never closes.",
    starts: "Doors open",
    ends: "Ends",
    rsvp: "Session booking",
    rsvpHint: "Off for an event without sessions to book.",
    words: "What attendees read",
    wordsBody: "Used in emails, the pack and the site.",
    variantAll: "All",
    variantAllHint: "The default text, for every language.",
    variantIdHint: "Only for Indonesian readers. Empty uses the default.",
    variantEnHint: "Only for English readers. Empty uses the default.",
    links: "Links and contacts",
    roles: "Role names",
    rolesBody: "What this organiser calls each staff role.",
    form: "Registration form",
    formBody: "Which profile questions are asked. Change only with the team.",
    hints: {
      profile_required: "Comma-separated question keys, such as company_name, job_title.",
      profile_identity:
        "The type given to everyone when the form doesn't ask: professional, creative_worker or brand_owner.",
      community_name: "The community in the membership question, such as Komitkabe.",
    },
    keys: {
      venue_name: "Venue",
      venue_address: "Venue address",
      venue_map_url: "Map link",
      parking: "Parking",
      dress_code: "Dress code",
      how_it_works: "How it works",
      qr_instructions: "QR instructions",
      before_you_come: "Before you come",
      event_guidelines: "Event guidelines (one per line)",
      photo_link: "Photo link",
      survey_url: "Survey link",
      sponsor_offers: "Sponsor offers",
      organizer_name: "Organiser",
      powered_by_name: "Powered by",
      contact_name: "Contact name",
      contact_phone: "Contact phone",
      role_label_frontdesk: "Desk role",
      role_label_monitor: "Board role",
      role_label_admin: "Admin role",
      role_label_superuser: "Superuser role",
      rsvp_enabled: "Session booking",
      profile_required: "Required questions",
      profile_identity: "Default type",
      community_name: "Community name",
    },
    unsaved: "Unsaved changes: {n}",
    allSaved: "Everything is saved",
    discard: "Discard",
    saved: "Event saved",
  },

  activities: {
    body: "The lineup. Cancelling keeps every booking and takes nobody new.",
    add: "Add activity",
    none: "No activities yet.",
    rsvpOff: "Session booking is off",
    rsvpOffBody: "Activities are listed, but nobody can book them until booking is switched on in Event.",
    cancelledBadge: "Cancelled",
    noCapacity: "No capacity",
    fill: "{confirmed}/{capacity} confirmed · {waitlisted} waiting",
    edit: "Edit",
    cancel: "Cancel activity",
    reinstate: "Reinstate",
    cancelled: "{title} cancelled",
    reinstated: "{title} reinstated",
    editTitle: "Edit activity",
    dialogBody: "Times are Jakarta time.",
    saved: "{title} saved",
    slugTaken: "Another activity already uses this slug.",
    belowConfirmed: "Can't be below the {n} already confirmed.",
    poster: "Poster",
    posterHint: "PNG, JPEG or WebP, up to 1 MB. Used when there's no cover link.",
    upload: "Upload poster",
    removePoster: "Remove poster",
    posterSaved: "Poster saved",
    posterRemoved: "Poster removed",
    f: {
      title: "Title",
      slug: "Slug",
      slugHint: "Lower case and dashes, such as panel-1.",
      host: "Host",
      room: "Room",
      allDayHint: "No slot and no capacity",
      starts: "Starts",
      ends: "Ends",
      capacity: "Capacity",
      capacityHint: "Empty keeps it closed to booking.",
      cover: "Cover image link",
      coverHint: "https:// address of an image.",
      description: "Description",
      highlights: "Highlights",
      highlightsHint: "One paragraph each; the first line is the title.",
    },
  },

  content: {
    body: "The logos and the registration form's pick lists.",
    logos: "Logos",
    logosBody: "PNG or JPEG, up to 1 MB. A replaced logo can take up to an hour to show everywhere.",
    industries: "Industries",
    industriesBody: "Switched off, an industry is hidden from the form; profiles that chose it keep it.",
    interests: "Interests",
    interestsBody: "Featured interests are shown first on the form.",
    slots: {
      organizer_logo: "Organiser",
      organizer_logo_dark: "Organiser, on dark",
      powered_by_logo: "Powered by",
      powered_by_logo_dark: "Powered by, on dark",
      platform_logo: "Platform",
      platform_logo_dark: "Platform, on dark",
    },
    noFile: "No file",
    slotHint: "Not uploaded",
    upload: "Upload",
    uploadFor: "Upload {slot}",
    replace: "Replace",
    remove: "Remove",
    uploaded: "Logo uploaded",
    removed: "Logo removed",
    tooBig: "The file is over 1 MB.",
    wrongType: "That file isn't a PNG or JPEG image.",
    newLabel: "New item",
    add: "Add",
    labelTaken: "That item already exists. Switch the old one back on instead.",
    emptyList: "Nothing in this list yet.",
    rename: "Name",
    up: "Move {label} up",
    down: "Move {label} down",
    featured: "Featured",
    active: "On",
    off: "Off",
  },

  email: {
    body: "The email templates, the reminder and thank-you campaigns, and paused sending.",
    templates: "Templates",
    campaigns: "Campaigns",
    holds: "Holds",
    language: "Language",
    default: "Default",
    pick: "Pick a template to edit it.",
    keys: {
      otp: "Verification code",
      rsvp_pack: "Attendee pack",
      rsvp_updated: "Booking updated",
      rsvp_promoted: "Off the waitlist",
      reminder_h1: "Reminder, day before",
      thank_you: "Thank you, day after",
    },
    about: {
      otp: "The six-digit code sent at registration and sign-in.",
      rsvp_pack: "The pack with the QR, sent once the profile is complete.",
      rsvp_updated: "Sent when someone changes their sessions after the pack.",
      rsvp_promoted: "Sent when a waitlisted place opens up.",
      reminder_h1: "The campaign the day before, to everyone verified.",
      thank_you: "The campaign the day after, to everyone who came.",
    },
    subject: "Subject",
    bodyHtml: "Body (HTML)",
    bodyHint: "The part between the shared header and footer. Insert fields from the list below.",
    fullHtml: "Whole email (HTML)",
    fullHint: "This version was saved as a whole email, header and footer included.",
    fields: "Fields",
    text: "Plain-text version",
    textHint: "Required for a whole-email version.",
    note: "Note",
    save: "Save and use",
    saved: "Saved as version {v}, now in use",
    invalid: "This version doesn't render, so it wasn't saved",
    preview: "Preview",
    previewNote:
      "The preview shows the version in use with sample data; inline logos only appear in real mail.",
    test: "Send a test",
    testHint: "Renders for a real attendee and sends to the address you give.",
    sendTest: "Send test",
    testQueued: "Test queued for {to}",
    noModel: "No verified attendee yet to render a test for.",
    newCampaign: "Schedule a campaign",
    newCampaignBody: "Starts as a draft. Arm it when the template and the details are ready.",
    kind: "Campaign",
    when: "Send at (Jakarta)",
    create: "Create draft",
    campaignCreated: "Draft created",
    noCampaigns: "No campaigns yet.",
    campaignLine: "{when} · {audience} · {n} recipients",
    audience: { verified: "everyone verified", checked_in: "everyone who came" },
    status: {
      draft: "Draft",
      armed: "Armed",
      sending: "Sending",
      paused: "Paused",
      sent: "Sent",
      cancelled: "Cancelled",
    },
    actions: { arm: "Arm", pause: "Pause", resume: "Resume", cancel: "Cancel" },
    progress: "{sent} sent · {failed} failed · of {of}",
    notReady: "Not ready to arm",
    badTransition: "That step isn't possible from the campaign's current state.",
    holdsBody:
      "Sending pauses itself when the email provider keeps failing. Release a hold once the cause is fixed.",
    noHolds: "Nothing is paused.",
    release: "Release",
    released: "{n} emails released",
    holdGone: "That hold was already released.",
  },
  survey: {
    body: "The questions on the /survey page, in both languages. Saving changes the page at once.",
    pageBody: "Results, merchandise and every response; the questions on the second tab.",
    tabs: { results: "Results", questions: "Questions" },
    results: {
      completed: "Responses",
      ofCheckedIn: "{share} of checked-in attendees",
      collected: "Merchandise collected",
      ofCompleted: "{share} of responses",
      waiting: "Waiting for merchandise",
      waitingNote: "Answered, not yet collected",
      status: "Survey",
      open: "Open",
      closed: "Closed",
      answered: "{n} answered",
      several: "several choices allowed",
      noAnswers: "No answers yet.",
      average: "average out of 5",
      score: "{n} out of 5",
      textNote: "Written answers: open a response below, or export.",
      typedUnder: "Typed under \"{option}\"",
      responses: "Responses",
      responsesBody: "Newest first. Open one to read every answer.",
      filter: "Show",
      all: "All",
      collectedShort: "Collected",
      waitingShort: "Not collected",
      search: "Search name, email, phone or institution",
      showing: "{n} of {of}",
      noMatch: "No response matches.",
      noResponses: "No responses yet.",
      who: "Attendee",
      org: "Institution",
      submitted: "Answered",
      merch: "Merchandise",
      submittedAt: "Answered {when}, in {lang}",
    },
    liveTitle: "Saved questions go live immediately",
    window: "The survey takes answers from {opens} to {closes} (Jakarta time).",
    noWindow: "No survey window is set yet.",
    none: "No questions yet. Add the first one below.",
    question: "Question {n}",
    moveUp: "Move up",
    moveDown: "Move down",
    remove: "Remove question",
    kind: "Type",
    kinds: {
      single: "One choice",
      multiple: "Several choices",
      rating: "Rating 1 to 5",
      text: "Free text",
    },
    required: "Required",
    promptId: "Question (Indonesian)",
    promptEn: "Question (English)",
    options: "Options",
    optionId: "Option {n} (Indonesian)",
    optionEn: "Option {n} (English)",
    removeOption: "Remove option {n}",
    addOption: "Add option",
    allowsText: "Ask them to specify (for Other / Lainnya)",
    ratingHint: "Attendees pick a score from 1 to 5.",
    textHint: "Attendees type an answer, up to 1000 characters.",
    add: "Add question",
    forceTitle: "People have already answered this survey",
    forceBody:
      "Their answers are kept with the wording they saw. Anyone with the page open must reload it before submitting. Save anyway?",
    forceConfirm: "Save anyway",
    fixFields: "Some fields need fixing; they are marked in red above.",
    unsaved: "Unsaved changes",
    allSaved: "{n} questions, all saved",
    discard: "Discard",
    saved: "Survey questions saved",
  },
} as const;

export type Dict = typeof en;
